import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Users, Copy, MessageCircle, Gift, Loader2, Sparkles } from "lucide-react";
import { api } from "@/lib/api";
import { TopBar } from "@/components/TopBar";

export default function Referrals() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get("/referrals").then(({ data }) => setData(data)).catch(() => setData({}));
  }, []);

  if (!data) return <div className="min-h-screen flex items-center justify-center"><Loader2 className="w-8 h-8 text-[#C9A24B] wt-spin" /></div>;

  const shareText = `Get your business online in minutes with Webtu. Use my code ${data.code} — we both get bonus days! ${data.share_url}`;
  const copyCode = async () => { try { await navigator.clipboard.writeText(data.code); toast.success("Code copied"); } catch { toast.error("Copy failed"); } };
  const copyLink = async () => { try { await navigator.clipboard.writeText(data.share_url); toast.success("Link copied"); } catch { toast.error("Copy failed"); } };

  return (
    <div className="min-h-screen">
      <TopBar />
      <div className="max-w-4xl mx-auto px-4 sm:px-6 py-8">
        <button onClick={() => navigate("/dashboard")} className="text-zinc-400 hover:text-white inline-flex items-center gap-1.5 text-sm mb-6">
          <ArrowLeft className="w-4 h-4" /> Dashboard
        </button>

        <div className="mb-6">
          <h1 className="font-display text-3xl font-bold flex items-center gap-3">
            <Users className="w-7 h-7 text-[#C9A24B]" /> Invite & Earn
          </h1>
          <p className="text-zinc-400 text-sm mt-1">Send Webtu to fellow shop owners — you both get bonus days on your next plan.</p>
        </div>

        <div className="grid md:grid-cols-2 gap-5 mb-8">
          {/* Rewards card */}
          <div className="rounded-3xl border border-[#C9A24B]/30 bg-gradient-to-br from-[#C9A24B]/10 to-transparent p-6">
            <Gift className="w-8 h-8 text-[#C9A24B] mb-3" />
            <div className="text-xs uppercase tracking-widest text-zinc-500 font-semibold">Your bonus balance</div>
            <div data-testid="referral-bonus-days" className="font-display text-4xl font-bold mt-1">
              {data.rewards?.days_credit || 0} <span className="text-lg text-zinc-400 font-normal">days free</span>
            </div>
            <p className="text-xs text-zinc-500 mt-2">
              {data.rewards?.successful_referrals || 0} friend{(data.rewards?.successful_referrals || 0) === 1 ? "" : "s"} activated a plan. +30 days each.
            </p>
            <p className="text-[11px] text-zinc-500 mt-3">Bonus days are applied automatically on your next Subscribe.</p>
          </div>

          {/* Share card */}
          <div data-testid="referral-share-card" className="rounded-3xl border border-[#27272A] bg-[#161616] p-6">
            <div className="text-xs uppercase tracking-widest text-zinc-500 font-semibold mb-2">Your referral code</div>
            <div className="flex items-center gap-2 mb-4">
              <div data-testid="referral-code" className="font-display text-3xl font-bold text-[#E5C378] tracking-wider">{data.code}</div>
              <button data-testid="referral-copy-code" onClick={copyCode}
                className="ml-auto p-2 rounded-lg bg-[#0A0A0A] border border-[#27272A] text-zinc-400 hover:text-[#C9A24B]"><Copy className="w-4 h-4" /></button>
            </div>
            <div className="text-xs uppercase tracking-widest text-zinc-500 font-semibold mb-2">Share link</div>
            <div className="flex items-center gap-2 mb-4">
              <div data-testid="referral-share-url" className="text-sm text-zinc-300 truncate flex-1">{data.share_url}</div>
              <button data-testid="referral-copy-link" onClick={copyLink}
                className="p-2 rounded-lg bg-[#0A0A0A] border border-[#27272A] text-zinc-400 hover:text-[#C9A24B]"><Copy className="w-4 h-4" /></button>
            </div>
            <a data-testid="referral-share-wa" href={`https://wa.me/?text=${encodeURIComponent(shareText)}`}
              target="_blank" rel="noreferrer"
              className="w-full inline-flex items-center justify-center gap-2 bg-[#25D366] hover:bg-[#22bf5b] text-[#04120a] font-bold py-3 rounded-xl transition-all">
              <MessageCircle className="w-4 h-4" /> Share on WhatsApp
            </a>
          </div>
        </div>

        <div className="mb-3 flex items-center gap-2">
          <div className="text-xs uppercase tracking-widest text-zinc-500 font-semibold">Friends you've invited</div>
        </div>
        {(!data.referrals || data.referrals.length === 0) ? (
          <div className="rounded-3xl border border-dashed border-[#27272A] py-16 text-center">
            <Sparkles className="w-8 h-8 text-[#C9A24B] mx-auto mb-3" />
            <h3 className="font-display text-xl font-bold">No invites yet</h3>
            <p className="text-zinc-400 text-sm mt-2 max-w-md mx-auto">Share your code with shop owners you know. When they activate a plan you both get 30 bonus days.</p>
          </div>
        ) : (
          <div className="space-y-2">
            {data.referrals.map((r, i) => (
              <div key={i} data-testid={`referral-item-${i}`} className="rounded-xl border border-[#27272A] bg-[#161616] p-4 flex items-center justify-between">
                <div>
                  <div className="font-semibold">{r.name || "Friend"}</div>
                  <div className="text-xs text-zinc-500">{r.phone_masked} · joined {new Date(r.created_at).toLocaleDateString("en-IN", { day: "2-digit", month: "short" })}</div>
                </div>
                <span className={`text-[10px] uppercase tracking-widest font-bold px-2 py-1 rounded-full ${r.converted ? "text-[#22C55E] bg-[#22C55E]/10 border border-[#22C55E]/30" : "text-zinc-500 border border-[#27272A]"}`}>
                  {r.converted ? "Activated" : "Signed up"}
                </span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
